/**
 * While the experiment runs: one block, one bar. "Running agent 3 of 10 ·
 * Scoring 9 of 30 checks" — the agent calls and the first scoring of their
 * outputs move together, so they read as one job. A re-score or retry has its
 * own bar (`ScoringProgress`).
 */

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/page";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { useActiveScoringRun, useExperimentAction } from "../hooks";
import { checksDone } from "../scoring/scoring-status";
import { doneCount, isLive } from "../status";
import type { ExperimentSummary } from "../types";
import { ProjectAction } from "@/features/projects/project-action";

export function RunProgress({ experiment: e }: { experiment: ExperimentSummary }) {
  const action = useExperimentAction(e.id);
  const notify = useUiStore((s) => s.notify);
  const active = useActiveScoringRun(e.id);
  if (!isLive(e.status)) return null;
  const scoring = active?.kind === "initial" ? active : null;
  const agentDone = doneCount(e.counts);
  const checks = scoring ? checksDone(scoring) : 0;
  const checksTotal = scoring?.counts.total ?? 0;

  return (
    <section className="box flex items-center gap-5 px-4 py-3.5" aria-label="Progress">
      <div className="flex flex-1 flex-col gap-2">
        <span className="text-[13px]">
          {e.status === "aggregating" ? (
            "Computing final scores"
          ) : (
            <>
              {e.status === "stopping" && "Stopping · "}
              Running agent <strong className="font-mono">{agentDone}</strong> of {e.counts.total}
              {scoring && (
                <>
                  {" "}
                  · Scoring <strong className="font-mono">{checks}</strong> of {checksTotal} checks
                </>
              )}
            </>
          )}
        </span>
        <Progress value={agentDone + checks} max={e.counts.total + checksTotal} label="Experiment progress" />
        <span className="text-xs text-muted">
          You can close this page. It keeps running, and scores below fill in as items finish.
        </span>
      </div>
      {e.status === "running" && (
        <ProjectAction permission="experiments:write">
          <Button
            disabled={action.isPending}
            onClick={() =>
              action.mutate("stop", {
                onSuccess: () => notify("Stopping. Scored items are kept."),
                onError: (err) => notify(errorText(err, "That did not work. Try again."), "error"),
              })
            }
          >
            Stop and keep results
          </Button>
        </ProjectAction>
      )}
    </section>
  );
}
